import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { Box, Button, Flex, Icon, Image, Spinner, Center } from '@chakra-ui/core';
import { motion, AnimatePresence } from 'framer-motion';
import { RiArrowLeftSLine, RiArrowRightSLine } from 'react-icons/ri';
import { FiDownload } from 'react-icons/fi';
import { AnswerFile, getDocumentFileName } from '../documentRenderer.types';

const MotionBox = motion.custom(Box);

export const slideVariants = {
  enter: (dir: number) => ({ x: dir > 0 ? 60 : -60, opacity: 0 }),
  center: { x: 0, opacity: 1, transition: { duration: 0.25, ease: 'easeOut' } },
  exit: (dir: number) => ({ x: dir > 0 ? -60 : 60, opacity: 0, transition: { duration: 0.2 } }),
};

interface SingleImageProps {
  file: AnswerFile;
}

const SingleImageRenderer: React.FC<SingleImageProps> = ({ file }) => {
  const [loading, setLoading] = useState(true);

  // Memoize alt text — getDocumentFileName() only reruns when file.name changes
  const alt = useMemo(() => getDocumentFileName(file.name), [file.name]);

  // load handler — avoids a new arrow function on every render
  const handleLoad = useCallback(() => setLoading(false), []);

  return (
    <Box borderRadius="10px" overflow="hidden" position="relative" bg="#455A64" minHeight="220px">
      {loading && (
        <Center position="absolute" inset={0}>
          <Spinner color="white" />
        </Center>
      )}
      <Image
        src={file.url}
        alt={alt}
        width="100%"
        maxHeight="220px"
        objectFit="cover"
        display={loading ? 'none' : 'block'}
        onLoad={handleLoad}
      />
    </Box>
  );
};

export const SingleImage = React.memo(SingleImageRenderer);

export interface ImageCarouselProps {
  files: AnswerFile[];
  initialIndex?: number;
  height?: string;
  objectFit?: any;
  showDownload?: boolean;
  onChangeIndex?: (index: number) => void;
}

const ImageCarouselComponent: React.FC<ImageCarouselProps> = ({
  files,
  initialIndex,
  height = '220px',
  objectFit = 'cover',
  showDownload = false,
  onChangeIndex,
}) => {
  const [index, setIndex] = useState(initialIndex ?? 0);
  const [direction, setDir] = useState(0);
  const [imageLoading, setImageLoading] = useState(true);

  // Cache blob URLs to avoid re-fetching the same image on repeated downloads
  const blobUrlCache = useRef<Map<string, string>>(new Map());

  // Revoke all cached blob URLs on unmount to prevent memory leaks
  useEffect(() => {
    const cache = blobUrlCache.current;
    return () => {
      cache.forEach((url) => window.URL.revokeObjectURL(url));
      cache.clear();
    };
  }, []);

  // Stable image-load handler — same reference across renders
  const handleImageLoad = useCallback(() => setImageLoading(false), []);

  // Stable navigation — go() is the single source of truth for index changes
  const go = useCallback(
    (next: number) => {
      setDir(next > index ? 1 : -1);
      setIndex(next);
      setImageLoading(true);
      onChangeIndex?.(next);
    },
    [index, onChangeIndex],
  );

  const prev = useCallback(() => go((index - 1 + files.length) % files.length), [go, index, files.length]);

  const next = useCallback(() => go((index + 1) % files.length), [go, index, files.length]);

  const dotHandlers = useMemo(() => files.map((_, i) => () => go(i)), [files, go]);

  const handleDownload = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      const activeFile = files[index];
      const fileUrl = activeFile.url;
      const fileName =
        getDocumentFileName(activeFile.name) ||
        /* istanbul ignore next */ fileUrl.split('/').pop() ||
        /* istanbul ignore next */ 'download';

      const triggerDownload = (href: string) => {
        const alink = document.createElement('a');
        alink.href = href;
        alink.download = fileName;
        document.body.appendChild(alink);
        alink.click();
        document.body.removeChild(alink);
      };

      const cached = blobUrlCache.current.get(fileUrl);
      // istanbul ignore if
      if (cached) {
        triggerDownload(cached);
        return;
      }

      fetch(fileUrl)
        .then((res) => res.blob())
        .then((blob) => {
          const blobUrl = window.URL.createObjectURL(blob);
          blobUrlCache.current.set(fileUrl, blobUrl);
          triggerDownload(blobUrl);
        })
        .catch(() => {
          triggerDownload(fileUrl);
        });
    },
    [files, index],
  );

  // guard against empty files before accessing files[index]
  // These memos must come before the early return but need a safe fallback
  const activeFile = useMemo(() => files[index] ?? null, [files, index]);
  const activeAlt = useMemo(() => (activeFile ? getDocumentFileName(activeFile.name) : ''), [activeFile]);

  if (files.length === 0) return null;

  return (
    <Box>
      <Box position="relative" borderRadius="10px" overflow="hidden" bg="#455A64" height={height}>
        <AnimatePresence initial={false} custom={direction}>
          <MotionBox
            key={index}
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
          >
            {imageLoading && (
              <Center position="absolute" inset={0} zIndex={1}>
                <Spinner color="white" size="xl" />
              </Center>
            )}
            <Image
              src={activeFile.url}
              alt={activeAlt}
              width="100%"
              height="100%"
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              objectFit={objectFit}
              display={imageLoading ? 'none' : 'block'}
              onLoad={handleImageLoad}
            />
          </MotionBox>
        </AnimatePresence>

        {showDownload && (
          <Button
            position="absolute"
            top={2}
            right={2}
            bg="#fff"
            borderRadius="6px"
            p={2}
            zIndex={30}
            onClick={handleDownload}
            cursor="pointer"
            _hover={{ bg: '#f0f0f0' }}
            aria-label="Download image"
            data-testid="downloadBtn"
            display="flex"
            alignItems="center"
            justifyContent="center"
            boxShadow="md"
          >
            <Icon as={FiDownload} color="#000" size={8} />
          </Button>
        )}

        {files.length > 1 && (
          <>
            <Box
              as="button"
              onClick={prev}
              position="absolute"
              left={2}
              top="50%"
              transform="translateY(-50%)"
              bg="rgba(0,0,0,0.48)"
              borderRadius="50%"
              width="28px"
              height="28px"
              display="flex"
              alignItems="center"
              justifyContent="center"
              cursor="pointer"
              border="none"
              zIndex={2}
              _hover={{ bg: 'rgba(0,0,0,0.68)' }}
              aria-label="Previous image"
            >
              <Icon as={RiArrowLeftSLine} color="white" size={5} />
            </Box>
            <Box
              as="button"
              onClick={next}
              position="absolute"
              right={2}
              top="50%"
              transform="translateY(-50%)"
              bg="rgba(0,0,0,0.48)"
              borderRadius="50%"
              width="28px"
              height="28px"
              display="flex"
              alignItems="center"
              justifyContent="center"
              cursor="pointer"
              border="none"
              zIndex={2}
              _hover={{ bg: 'rgba(0,0,0,0.68)' }}
              aria-label="Next image"
            >
              <Icon as={RiArrowRightSLine} color="white" size={5} />
            </Box>
          </>
        )}
      </Box>

      {files.length > 1 && (
        <Flex justify="center" mt={2} style={{ gap: '6px' }}>
          {files.map((_, i) => (
            <Box
              key={`${files[i].name}`}
              as="button"
              onClick={dotHandlers[i]}
              width="7px"
              height="7px"
              borderRadius="50%"
              border="none"
              cursor="pointer"
              p={0}
              bg={i === index ? '#2563EB' : 'gray.300'}
              style={{ transition: 'background 0.2s' }}
              aria-label={`Go to image ${i + 1}`}
            />
          ))}
        </Flex>
      )}
    </Box>
  );
};

export const ImageCarousel = React.memo(ImageCarouselComponent);
export default ImageCarousel;
